/* Parents.
   /account/family  the student shares a read-only progress page with a parent (in the parent's language, showing what the
                    student chooses) and sends payment links so someone else can pay for a pass. Every link can be
                    stopped at once. ?pay=<plan> (from the Plans page) opens the payment-link form with that plan chosen.
   /family          the parent's page (?token=): no account needed, read-only. A parent can add their own WhatsApp number
                    there for reminders about what the page shows (a code to the number first). */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  SELLING,
  LANG_NAMES, WEB_ROUTES, asLang, isLang, planById,
  type FamilyLink, type FamilyResponse, type Lang, type ParentStatus, type ParentView as ParentViewData, type ParentWhatsAppResponse,
} from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { useLocation } from '../app/nav';
import { useMe } from '../app/session';
import { useErrorHandler, useLoad } from '../app/hooks';
import { Icon } from '../app/icons';
import { Alert, Button, Checkbox, Chip, CodeField, CopyButton, Live, LoadError, Loading, PageHeader, Section, SelectField, TextField, type Tone } from '../app/ui';
import { api, ApiError, errorAction } from '../lib/api';
import { countryName, formatDate, formatDateTime } from '../lib/format';
import { covered } from '../lib/passes';
import { sixDigits } from '../lib/forms';
import { readLang } from '../lib/storage';
import { LANG_OPTIONS, translate } from '../lib/i18n';

/* what a payment link can pay for: Apply & Arrive (Apply and Arrive on their own aren't sold since 31 October 2026) */
type Paid = 'bundle';
const PAID: Paid[] = ['bundle'];
const LINK_TONE: Record<FamilyLink['status'], Tone> = { active: 'green', used: 'blue', expired: 'gray', revoked: 'gray' };

/* the message a parent gets, in the link's language (not the student's) */
export function shareText(link: Pick<FamilyLink, 'kind' | 'lang' | 'plan' | 'url'>): { subject: string; body: string } {
  const l = link.lang, url = link.url ?? '';
  return link.kind === 'view'
    ? { subject: translate(l, 'fam.msg.viewSubject'), body: translate(l, 'fam.msg.view', { url }) }
    : { subject: translate(l, 'fam.msg.paySubject'), body: translate(l, 'fam.msg.pay', { url, plan: link.plan ? planById(link.plan).name[l] : '' }) };
}
export const whatsappHref = (text: string) => `https://wa.me/?text=${encodeURIComponent(text)}`;
export const mailtoHref = (subject: string, body: string) => `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

/* ---------- the student's page ---------- */
export function Family() {
  const { t } = useT();
  const me = useMe();
  const { query, hash } = useLocation();
  const links = useLoad(s => api.get<FamilyResponse>('/api/family', { signal: s }).then(r => r.links), []);
  const [fresh, setFresh] = useState<string | null>(null);
  const askedPlan = query.get('pay');
  const toPay = !!askedPlan || hash === '#fam-pay';

  /* from the Plans page: straight to the payment-link form */
  useEffect(() => {
    if (!toPay || links.status !== 'ok') return;
    document.getElementById('fam-pay')?.scrollIntoView({ block: 'start' });
    document.getElementById('fp-plan')?.focus({ preventScroll: true });
  }, [toPay, links.status]);

  const added = (l: FamilyLink) => {
    if (links.status === 'ok') links.set([l, ...links.data]);
    setFresh(l.id);
  };
  const changed = (l: FamilyLink) => { if (links.status === 'ok') links.set(links.data.map(x => (x.id === l.id ? l : x))); };

  return (
    <>
      <PageHeader title={t('fam.title')} lead={t('fam.lead')} back={{ to: WEB_ROUTES.account, label: t('nav.account') }} />

      <Section title={t('fam.share.title')} id="fam-share">
        <div className="card stack">
          <div>
            <h3 className="card__title">{t('fam.share.sees')}</h3>
            <ul className="ticks">
              <li>{t('fam.share.see1')}</li>
              <li>{t('fam.share.see2')}</li>
              <li>{t('fam.share.see3')}</li>
              <li>{t('fam.share.see4')}</li>
            </ul>
            <p className="muted mt-sm">{t('fam.share.never')}</p>
          </div>
          <ShareForm onAdded={added} />
        </div>
      </Section>

      {/* nothing to pay for since everything became free (3 November 2026) */}
      {SELLING && (
        <Section title={t('fam.pay.title')} id="fam-pay">
          <PayForm have={covered(me.plans.passes)} asked={PAID.includes(askedPlan as Paid) ? askedPlan as Paid : null} onAdded={added} />
        </Section>
      )}

      <Section title={t('fam.links.title')} id="fam-links">
        {links.status === 'loading' ? <Loading /> : links.status === 'error' ? <LoadError message={links.message} retry={links.reload} /> :
          links.data.length === 0 ? <p className="empty">{t('fam.links.empty')}</p> : (
            <ul className="famlinks">
              {links.data.map(l => <li key={l.id}><LinkCard link={l} fresh={fresh === l.id} onChange={changed} /></li>)}
            </ul>
          )}
      </Section>
    </>
  );
}

function useCreate(onAdded: (l: FamilyLink) => void) {
  const { t } = useT();
  const handle = useErrorHandler();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = async (body: Record<string, unknown>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ link: FamilyLink }>('/api/family/links', body);
      onAdded(r.link);
      requestAnimationFrame(() => document.getElementById(`fl-${r.link.id}`)?.focus());
    } catch (e) {
      if (e instanceof ApiError && e.code === 'forbidden') setError(t('fam.tooMany'));
      else if (e instanceof ApiError && e.code === 'already_paid') setError(t('err.already_paid'));
      else { const msg = handle(e); if (msg) setError(msg); }
    } finally { setBusy(false); }
  };
  return { busy, error, create };
}

function ShareForm({ onAdded }: { onAdded: (l: FamilyLink) => void }) {
  const { t, lang } = useT();
  const [pLang, setPLang] = useState<Lang>(lang);
  const [label, setLabel] = useState('');
  const [deadlines, setDeadlines] = useState(true);
  const [decisions, setDecisions] = useState(true);
  const { busy, error, create } = useCreate(onAdded);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void create({ kind: 'view', lang: pLang, label: label.trim() || null, show_deadlines: deadlines, show_decisions: decisions }).then(() => setLabel(''));
  };
  return (
    <form className="stack" noValidate onSubmit={submit} aria-label={t('fam.share.title')}>
      <SelectField id="fs-lang" label={t('fam.form.lang')} value={pLang} placeholder={false} onChange={v => setPLang(asLang(v))} options={LANG_OPTIONS} />
      <TextField id="fs-label" label={t('fam.form.label')} hint={t('fam.form.labelHint')} optional value={label} onChange={setLabel} maxLength={40} autoComplete="off" />
      <Checkbox id="fs-deadlines" checked={deadlines} onChange={setDeadlines} label={t('fam.form.deadlines')} description={t('fam.form.deadlinesNote')} />
      <Checkbox id="fs-decisions" checked={decisions} onChange={setDecisions} label={t('fam.form.decisions')} description={t('fam.form.decisionsNote')} />
      {error && <Alert>{error}</Alert>}
      <Button type="submit" busy={busy}><Icon.people />{t('fam.form.create')}</Button>
    </form>
  );
}

function PayForm({ have, asked, onAdded }: { have: { apply: boolean; arrive: boolean }; asked: Paid | null; onAdded: (l: FamilyLink) => void }) {
  const { t, lang } = useT();
  const open = PAID.filter(() => !have.apply || !have.arrive);
  const [plan, setPlan] = useState<Paid | ''>(asked && open.includes(asked) ? asked : open[0] ?? '');
  const [pLang, setPLang] = useState<Lang>(lang);
  const { busy, error, create } = useCreate(onAdded);
  if (!open.length) return <p className="empty">{t('fam.pay.covered')}</p>;
  const submit = (e: FormEvent) => { e.preventDefault(); if (plan) void create({ kind: 'pay', plan, lang: pLang }); };
  return (
    <form className="card stack" noValidate onSubmit={submit} aria-labelledby="fam-pay-title">
      <p>{t('fam.pay.lead')}</p>
      {open.length > 1 ? <SelectField id="fp-plan" label={t('fam.pay.plan')} value={plan} placeholder={false} onChange={v => setPlan(v as Paid)}
        options={open.map(p => ({ value: p, label: planById(p).name[lang] }))} />
        : <p className="fp-plan"><span className="fine">{t('fam.pay.plan')}</span> <b>{planById(open[0]).name[lang]}</b></p>}
      <SelectField id="fp-lang" label={t('fam.form.lang')} value={pLang} placeholder={false} onChange={v => setPLang(asLang(v))} options={LANG_OPTIONS} />
      <p className="fine">{t('fam.pay.buyer')}</p>
      {error && <Alert>{error}</Alert>}
      <Button type="submit" busy={busy}><Icon.pass />{t('fam.pay.create')}</Button>
    </form>
  );
}

function LinkCard({ link, fresh, onChange }: { link: FamilyLink; fresh: boolean; onChange: (l: FamilyLink) => void }) {
  const { t, lang } = useT();
  const handle = useErrorHandler();
  const [busy, setBusy] = useState<'stop' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [decisions, setDecisions] = useState(link.show_decisions);
  const [deadlines, setDeadlines] = useState(link.show_deadlines);
  const urlRef = useRef<HTMLParagraphElement>(null);
  const active = link.status === 'active' && !!link.url;
  const title = link.kind === 'view' ? t('fam.link.view') : t('fam.link.pay', { plan: link.plan ? planById(link.plan).name[lang] : '' });
  const msg = shareText(link);

  const call = async (what: 'stop' | 'save', fn: () => Promise<{ link: FamilyLink }>) => {
    if (busy) return;
    setBusy(what);
    setError(null);
    setSaved(false);
    try {
      const r = await fn();
      onChange(r.link);
      if (what === 'save') { setSaved(true); setEditing(false); }
    } catch (e) { const m = handle(e); if (m) setError(m); }
    finally { setBusy(null); }
  };
  const stop = () => call('stop', () => api.post(`/api/family/links/${encodeURIComponent(link.id)}/revoke`));
  const save = () => call('save', () => api.patch(`/api/family/links/${encodeURIComponent(link.id)}`, { show_decisions: decisions, show_deadlines: deadlines }));

  return (
    <article className="card famlink" id={`fl-${link.id}`} tabIndex={-1} aria-labelledby={`fl-${link.id}-t`}>
      <div className="famlink__head">
        <h3 className="card__title" id={`fl-${link.id}-t`}>{title}</h3>
        <Chip tone={LINK_TONE[link.status]}>{t(`fam.link.status.${link.status}`)}</Chip>
      </div>
      <p className="famlink__meta">
        {link.label && <span><bdi>{t('fam.link.for', { label: link.label })}</bdi></span>}
        <span>{t('fam.link.inLang', { language: LANG_NAMES[link.lang] })}</span>
        {active && <span>{t('fam.link.until', { date: formatDate(link.expires_at, lang) })}</span>}
        {link.kind === 'view' && <span>{link.last_viewed_at ? t('fam.link.opened', { date: formatDate(link.last_viewed_at, lang) }) : t('fam.link.notOpened')}</span>}
      </p>
      {fresh && <Live><Alert tone="success">{t('fam.created')}</Alert></Live>}
      {active && (
        <>
          <p className="famlink__url" dir="ltr" ref={urlRef}>{link.url}</p>
          <div className="share-actions">
            <CopyButton text={link.url as string} label={t('fam.link.copy')} selectRef={urlRef} />
            <a className="btn btn--secondary btn--small" href={whatsappHref(msg.body)} target="_blank" rel="noopener noreferrer">
              {t('fam.link.whatsapp')}<span className="sr-only"> {t('common.opensNew')}</span>
            </a>
            <a className="btn btn--secondary btn--small" href={mailtoHref(msg.subject, msg.body)}><Icon.mail />{t('fam.link.email')}</a>
            <Button variant="danger" small busy={busy === 'stop'} onClick={() => void stop()}>{link.kind === 'view' ? t('fam.link.stop') : t('fam.link.cancel')}</Button>
          </div>
          {link.kind === 'view' && !editing && (
            <p><button type="button" className="linkbtn" onClick={() => setEditing(true)}>{t('fam.link.edit')}</button></p>
          )}
          {link.kind === 'view' && editing && (
            <div className="stack">
              <Checkbox id={`fl-${link.id}-dl`} checked={deadlines} onChange={setDeadlines} label={t('fam.form.deadlines')} description={t('fam.form.deadlinesNote')} />
              <Checkbox id={`fl-${link.id}-dc`} checked={decisions} onChange={setDecisions} label={t('fam.form.decisions')} description={t('fam.form.decisionsNote')} />
              <div className="btnrow">
                <Button small busy={busy === 'save'} onClick={() => void save()}>{t('common.save')}</Button>
                <Button variant="gray" small onClick={() => { setEditing(false); setDecisions(link.show_decisions); setDeadlines(link.show_deadlines); }}>{t('common.cancel')}</Button>
              </div>
            </div>
          )}
        </>
      )}
      <Live>{saved ? <p className="hint">{t('fam.link.saved')}</p> : null}</Live>
      {error && <Alert>{error}</Alert>}
    </article>
  );
}

/* ---------- the parent's page ---------- */
const STATUS_TONE: Record<ParentStatus, Tone> = {
  applying: 'gray', submitted: 'teal', offer: 'green', offer_conditional: 'green', waitlisted: 'orange', deferred: 'orange',
  not_admitted: 'gray', accepted: 'green', declined: 'gray', withdrawn: 'gray',
};

/* pages opened from a link: kept out of search engines */
export function useNoIndex() {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, []);
}

export function ParentView() {
  const { t, lang, setLang } = useT();
  const { query } = useLocation();
  const token = query.get('token') ?? '';
  const [data, setData] = useState<ParentViewData | null>(null);
  const [load, setLoad] = useState<'loading' | 'ok' | 'invalid' | 'error'>(token ? 'loading' : 'invalid');
  const [n, setN] = useState(0);
  useNoIndex();

  useEffect(() => {
    if (!token) { setLoad('invalid'); return; }
    const ac = new AbortController();
    api.get<ParentViewData>(`/api/family/view?token=${encodeURIComponent(token)}`, { signal: ac.signal })
      .then(v => {
        setData(v);
        setLoad('ok');
        /* the page opens in the language the student chose for it, unless this browser has a chosen language */
        if (!readLang() && isLang(v.lang)) setLang(v.lang, { remember: false });
      })
      .catch(e => {
        if (ac.signal.aborted) return;
        setLoad(e instanceof ApiError && (e.code === 'not_found' || e.code === 'invalid' || e.status === 404) ? 'invalid' : 'error');
      });
    return () => ac.abort();
  }, [token, n, setLang]);

  if (load === 'loading') return <div className="narrow-wide"><PageHeader title={t('fam.view.title')} /><Loading /></div>;
  if (load === 'invalid') return <div className="narrow"><PageHeader title={t('fam.view.invalid.title')} lead={t('fam.view.invalid.text')} /></div>;
  if (load === 'error' || !data) {
    return (
      <div className="narrow">
        <PageHeader title={t('fam.view.title')} />
        <Alert>{t('err.network')} <button type="button" className="linkbtn" onClick={() => setN(x => x + 1)}>{t('common.retry')}</button></Alert>
      </div>
    );
  }
  const name = data.student;
  const passes = (['apply', 'arrive'] as const).filter(p => data.passes[p]);
  return (
    <div className="narrow-wide parent">
      <PageHeader title={name ? t('fam.view.titleName', { name }) : t('fam.view.title')} lead={name ? t('fam.view.lead', { name }) : t('fam.view.leadNoName')} />

      <Section title={t('fam.view.unis')} id="pv-unis" foot={data.show_decisions ? undefined : t('fam.view.decisionsHidden')}>
        {data.apps.length === 0 ? <p className="empty">{t('fam.view.unisEmpty')}</p> : (
          <ul className="group list">
            {data.apps.map((a, i) => (
              <li key={i}>
                <div className="row">
                  <span className="row__main">
                    {/* official university names stay as they are, in every language */}
                    <span className="row__title"><bdi>{a.school_name}</bdi></span>
                    {a.country && <span className="row__sub">{countryName(a.country, lang)}</span>}
                  </span>
                  <span className="row__end"><Chip tone={STATUS_TONE[a.status]}>{t(`fam.status.${a.status}`)}</Chip></span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {data.show_deadlines && (
        <Section title={t('fam.view.deadlines')} id="pv-deadlines">
          {data.deadlines.length === 0 ? <p className="empty">{t('fam.view.deadlinesEmpty')}</p> : (
            <ul className="group list">
              {data.deadlines.map((d, i) => (
                <li key={i}>
                  <div className="row">
                    <span className="row__main">
                      <span className="row__title"><bdi>{d.title}</bdi></span>
                      <span className="row__sub">{t('fam.view.due', { date: formatDate(d.due_date, lang) })}</span>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      <Section title={t('fam.view.passes')} id="pv-passes">
        {passes.length === 0 ? <p className="empty">{t('fam.view.noPass')}</p> : (
          <ul className="group list list--icons">
            {passes.map(p => (
              <li key={p}><div className="row"><span className="tile tile--orange"><Icon.pass /></span><span className="row__main"><span className="row__title">
                {t('fam.view.passUntil', { plan: planById(p).name[lang], date: formatDate(data.passes[p], lang) })}
              </span></span></div></li>
            ))}
          </ul>
        )}
      </Section>

      {data.whatsapp_available && <ParentWhatsApp token={token} name={name} />}

      <div className="section">
        <p className="fine">{t('fam.view.updated', { date: formatDateTime(data.generated_at, lang) })}</p>
        <p className="fine">{t('fam.view.about')}</p>
      </div>
    </div>
  );
}

const browserZone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } };

/* the parent's own WhatsApp reminders: their number, an unticked agreement, a code sent to the number, the code typed here */
function ParentWhatsApp({ token, name }: { token: string; name: string | null }) {
  const { t, lang } = useT();
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'form' | 'code' | 'done'>('form');
  const [masked, setMasked] = useState('');
  const [busy, setBusy] = useState<'' | 'send' | 'verify'>('');
  const [error, setError] = useState<{ phone?: string; consent?: string; code?: string; form?: string }>({});
  const focus = (id: string) => setTimeout(() => document.getElementById(id)?.focus(), 0);

  const fail = (e: unknown, field: 'phone' | 'code' | 'form') => {
    if (e instanceof ApiError) {
      const body = (e.body ?? {}) as { reason?: string };
      if (e.code === 'invalid' && e.field === 'phone') {
        setStage('form');
        setError({ phone: t(body.reason === 'not_on_whatsapp' ? 'fam.wa.phoneNotWhatsApp' : body.reason === 'not_mobile' ? 'fam.wa.phoneNotMobile' : 'fam.wa.phoneInvalid') });
        focus('pw-phone');
        return;
      }
      if (e.code === 'bad_code') { setError({ code: t('fam.wa.badCode') }); focus('pw-code'); return; }
    }
    setError({ [field]: publicError(e, lang) });
  };

  const send = async (ev?: FormEvent) => {
    ev?.preventDefault();
    if (busy) return;
    if (!phone.trim()) { setStage('form'); setError({ phone: t('fam.wa.phoneInvalid') }); focus('pw-phone'); return; }
    if (!consent) { setStage('form'); setError({ consent: t('fam.wa.consentNeeded') }); focus('pw-consent'); return; }
    setBusy('send'); setError({});
    try {
      const r = await api.post<ParentWhatsAppResponse>('/api/family/whatsapp', { token, phone, lang, tz: browserZone(), consent: true });
      setMasked(r.phone ?? '');
      if (r.status === 'active') setStage('done');
      else { setStage('code'); setCode(''); focus('pw-code'); }
    } catch (e) { fail(e, stage === 'code' ? 'form' : 'phone'); } finally { setBusy(''); }
  };

  const verify = async (ev: FormEvent) => {
    ev.preventDefault();
    if (busy) return;
    if (code.length !== 6) { setError({ code: t('v.code') }); focus('pw-code'); return; }
    setBusy('verify'); setError({});
    try {
      const r = await api.post<ParentWhatsAppResponse>('/api/family/whatsapp/verify', { token, code });
      setMasked(r.phone ?? masked);
      setStage('done');
    } catch (e) { setCode(''); fail(e, 'code'); } finally { setBusy(''); }
  };

  return (
    <Section title={t('fam.wa.title')} id="pv-whatsapp" foot={stage === 'done' ? undefined : t('fam.wa.privacy')}>
      {stage === 'done' ? <Live><Alert tone="success">{t('fam.wa.done', { phone: masked })}</Alert></Live> : (
        <>
          <p className="hint mb">{name ? t('fam.wa.lead', { name }) : t('fam.wa.leadNoName')}</p>
          {stage === 'code' ? (
            <form className="card stack" noValidate onSubmit={e => void verify(e)} aria-labelledby="pv-whatsapp-title">
              <p>{t('fam.wa.sent', { phone: masked })}</p>
              <CodeField id="pw-code" label={t('fam.wa.code')} value={code} onChange={v => { setCode(sixDigits(v)); setError({}); }} error={error.code} autoFocus />
              {error.form && <Alert>{error.form}</Alert>}
              <div className="actions">
                <Button type="submit" busy={busy === 'verify'}>{t('fam.wa.confirm')}</Button>
                <Button variant="gray" small busy={busy === 'send'} onClick={() => void send()}>{t('fam.wa.resend')}</Button>
                <Button variant="plain" small onClick={() => { setStage('form'); setError({}); focus('pw-phone'); }}>{t('fam.wa.other')}</Button>
              </div>
            </form>
          ) : (
            <form className="card stack" noValidate onSubmit={e => void send(e)} aria-labelledby="pv-whatsapp-title">
              <TextField id="pw-phone" label={t('fam.wa.phone')} hint={t('fam.wa.phoneHint')} value={phone} onChange={v => { setPhone(v); setError({}); }}
                error={error.phone} ltr type="tel" inputMode="tel" autoComplete="tel" />
              <Checkbox id="pw-consent" checked={consent} onChange={v => { setConsent(v); setError({}); }}
                label={name ? t('fam.wa.consent', { name }) : t('fam.wa.consentNoName')} error={error.consent} required />
              {error.form && <Alert>{error.form}</Alert>}
              <div className="actions"><Button type="submit" busy={busy === 'send'}>{t('fam.wa.send')}</Button></div>
            </form>
          )}
        </>
      )}
    </Section>
  );
}

/* kept for pages that report API errors without redirecting to sign-in (they have no session) */
export function publicError(e: unknown, lang: Lang): string {
  const a = errorAction(e, '', ['signed_out', 'mfa_required', 'guardian_consent_required', 'plan_required']);
  return a.kind === 'message' ? translate(lang, a.key) : translate(lang, 'err.server_error');
}
